import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Container, Row, Col, Card, Button, Badge, Form, Alert, Modal, InputGroup } from 'react-bootstrap';
import { useNavigate, useLocation } from 'react-router-dom';
import { FaShoppingCart, FaPlus, FaMinus, FaTrash, FaArrowLeft, FaCheck, FaBox, FaUser, FaFileAlt, FaSearch, FaLock } from 'react-icons/fa';
import supabase from '../../supabaseClient';
import { useAuth } from '../../hooks/useAuth';
import '../../styles/order-page.css';

// Componente auxiliar para um item do carrinho
const CartItem = ({ item, onUpdateQuantity, onRemove, formatCurrency, getItemFinalPrice }) => {
  const finalPrice = getItemFinalPrice(item);
  const hasDiscount = item.discount && item.discount > 0;
  const originalPrice = item.customPrice || item.price;

  const handleRemove = () => {
    const isCredentialWorkerOrProvider = item.category === 'Credencial' || item.category === 'Montador' || item.category === 'Prestador';
    
    if (isCredentialWorkerOrProvider) {
      let message = '';
      switch (item.category) {
        case 'Credencial':
          message = `Tem certeza que deseja remover a credencial "${item.name}"?\n\nAVISO: Esta ação irá excluir permanentemente a credencial do sistema.`;
          break;
        case 'Montador':
          message = `Tem certeza que deseja remover o montador "${item.name}"?\n\nAVISO: Esta ação irá excluir permanentemente o montador do sistema.`;
          break;
        case 'Prestador':
          message = `Tem certeza que deseja remover o prestador "${item.name}"?\n\nAVISO: Esta ação irá excluir permanentemente o prestador do sistema.`;
          break;
        default:
          message = `Tem certeza que deseja remover "${item.name}"?`;
      }
      
      if (window.confirm(message)) {
        onRemove(item.id);
      }
    } else {
      onRemove(item.id);
    }
  };

  return (
    <div className="cart-item">
      <div className="cart-item-left">
        <div className="cart-item-details">
          <div className="d-flex align-items-center">
            <h6 className="mb-1">{item.name}</h6>
          </div>
          <p className="cart-item-description">{item.description}</p>
          
          <div className="cart-item-pricing">
            {hasDiscount ? (
              <>
                <p className="cart-item-original-price text-decoration-line-through text-muted mb-1">
                  {formatCurrency(originalPrice)}
                </p>
                <p className="cart-item-price text-success mb-1">
                  {formatCurrency(finalPrice)} por unidade
                </p>
                <div className="discount-info">
                  <Badge bg="success" pill>
                    {item.discountType === 'percentage' 
                      ? `${item.discount}% OFF`
                      : `Desconto: ${formatCurrency(item.discount)}`
                    }
                  </Badge>
                </div>
              </>
            ) : (
              <p className="cart-item-price">
                {formatCurrency(finalPrice)} por unidade
                {item.customPrice && item.customPrice !== item.price && (
                  <Badge bg="info" pill className="ms-2">Preço Ajustado</Badge>
                )}
              </p>
            )}
          </div>
          
          {item.category && (
            <Badge bg="secondary" pill className="mt-1">
              {item.category}
              {(item.category === 'Credencial' || item.category === 'Montador' || item.category === 'Prestador') && (
                <span className="ms-1" title="A remoção deste item excluirá o registro do sistema">⚠️</span>
              )}
            </Badge>
          )}
        </div>
      </div>
      
      <div className="cart-item-right">
        <div className="cart-quantity-display">
          <span className="cart-quantity-label">Quantidade:</span>
          <span className="cart-quantity-value">{item.quantity}</span>
        </div>
        
        <div className="cart-item-total">
          <div className="cart-item-total-price">
            {formatCurrency(finalPrice * item.quantity)}
            {hasDiscount && (
              <div className="cart-item-savings text-success">
                <small>
                  Economia: {formatCurrency((originalPrice - finalPrice) * item.quantity)}
                </small>
              </div>
            )}
          </div>
          <button 
            className="cart-remove-btn"
            onClick={handleRemove}
            title={
              (item.category === 'Credencial' || item.category === 'Montador' || item.category === 'Prestador') 
                ? "ATENÇÃO: Remover irá excluir permanentemente do sistema" 
                : "Remover item do carrinho"
            }
          >
            <FaTrash size={10} /> Remover
          </button>
        </div>
      </div>
    </div>
  );
};

// Componente auxiliar para o resumo do carrinho
const CartSummary = ({ cart, calculateCartTotal, calculateTotalDiscount, getTotalItems, formatCurrency }) => {
  return (
    <div className="order-page-cart-summary">
      <FaShoppingCart className="order-page-cart-icon" />
      <span className="order-page-items-count">
        {getTotalItems()} {getTotalItems() === 1 ? 'item' : 'itens'}
      </span>
      <h4 className="order-page-cart-total">{formatCurrency(calculateCartTotal())}</h4>
    </div>
  );
};

const NewOrderPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  
  // Estados principais
  const [cart, setCart] = useState([]);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [orderSubmitted, setOrderSubmitted] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Estados para dados
  const [expositores, setExpositores] = useState([]);
  const [eventos, setEventos] = useState([]);
  const [eventosVinculados, setEventosVinculados] = useState([]);
  const [userExpositorData, setUserExpositorData] = useState(null); // NOVO: Dados do expositor logado
  
  // Estados para UI
  const [searchTerm, setSearchTerm] = useState('');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  
  // Estados para controle de auto-preenchimento
  const [dataSource, setDataSource] = useState(''); // 'logged_expositor', 'cart_credential', 'cart_montador', 'cart_prestador', 'manual'
  const [fieldsLocked, setFieldsLocked] = useState(false); // NOVO: Controla se os campos estão bloqueados
  
  const [customerInfo, setCustomerInfo] = useState({
    company: '',
    contact: '',
    email: '',
    phone: '',
    event: '',
    notes: ''
  });

  // FUNÇÃO AUXILIAR: Verificar se a tabela caex_montadores tem o campo pedido_id
  const verifyMontadoresTableStructure = useCallback(async () => {
    try {
      console.log('Verificando estrutura da tabela caex_montadores...');
      
      // Tentar fazer um select simples para verificar se a coluna pedido_id existe
      const { data, error } = await supabase
        .from('caex_montadores')
        .select('id, pedido_id')
        .limit(1);
      
      if (error) {
        if (error.message.includes('column "pedido_id" does not exist')) {
          console.error('ERRO: A coluna pedido_id não existe na tabela caex_montadores!');
          console.error('Você precisa adicionar a coluna pedido_id na tabela caex_montadores:');
          console.error('ALTER TABLE caex_montadores ADD COLUMN pedido_id INTEGER REFERENCES caex_pedidos(id);');
          return false;
        } else {
          console.error('Erro ao verificar tabela caex_montadores:', error);
          return false;
        }
      }
      
      console.log('Estrutura da tabela caex_montadores verificada com sucesso');
      return true;
    } catch (error) {
      console.error('Erro ao verificar estrutura da tabela:', error);
      return false;
    }
  }, []);

  // Chame esta função ao carregar o componente para verificar a estrutura
  useEffect(() => {
    if (user?.id) {
      verifyMontadoresTableStructure();
    }
  }, [user, verifyMontadoresTableStructure]);

  // Verificar se usuário é expositor ao carregar
  const checkUserExpositorData = useCallback(async () => {
    if (!user?.id) return null;
    
    try {
      const { data: expositorData, error } = await supabase
        .from('caex_expositores')
        .select('*')
        .eq('usuario_id', user.id)
        .eq('ativo', true)
        .single();
      
      if (error) {
        // Usuário não é expositor (é admin ou outro tipo)
        console.log('Usuário não é expositor:', error.message);
        return null;
      }
      
      console.log('Usuário é expositor:', expositorData);
      return expositorData;
    } catch (error) {
      console.error('Erro ao verificar dados do expositor:', error);
      return null;
    }
  }, [user]);

  // Função otimizada para carregar dados iniciais
  const loadInitialData = useCallback(async () => {
    if (!user?.id) {
      navigate('/login');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Executar todas as consultas em paralelo
      const [
        { data: expositoresData, error: expositoresError },
        { data: eventosData, error: eventosError },
        { data: cartData, error: cartError },
        userExpositorData
      ] = await Promise.all([
        supabase
          .from('caex_expositores')
          .select('*')
          .eq('ativo', true)
          .order('nome_empresa', { ascending: true }),
        
        supabase
          .from('caex_eventos')
          .select('*')
          .eq('ativo', true)
          .order('data_inicio', { ascending: false }),
        
        supabase
          .from('caex_carrinho_pedido')
          .select(`
            *,
            caex_produtos (
              id,
              nome,
              descricao,
              preco,
              categoria_id,
              estoque,
              visivel_no_catalogo,
              ativo
            )
          `)
          .eq('usuario_id', user.id)
          .order('created_at', { ascending: true }),
        
        checkUserExpositorData()
      ]);

      // Verificar erros
      if (expositoresError) throw new Error('Erro ao carregar expositores: ' + expositoresError.message);
      if (eventosError) throw new Error('Erro ao carregar eventos: ' + eventosError.message);
      if (cartError) throw new Error('Erro ao carregar carrinho: ' + cartError.message);

      // Definir dados
      setExpositores(expositoresData || []);
      setEventos(eventosData || []);
      setUserExpositorData(userExpositorData);

      // Transformar dados do carrinho - ESTRUTURA CORRIGIDA
      const formattedCart = (cartData || []).map(item => {
        // Extrair dados extras primeiro
        const dadosExtras = item.dados_extras || {};
        
        // Determinar a categoria correta baseada nos dados extras
        let categoryFromExtras = null;
        if (dadosExtras.category) {
          categoryFromExtras = dadosExtras.category;
        }
        
        console.log(`Processando item do carrinho - Produto ID: ${item.produto_id}, Category extras: ${categoryFromExtras}`);
        
        return {
          id: item.produto_id,
          name: item.caex_produtos.nome,
          description: item.caex_produtos.descricao,
          price: parseFloat(item.preco_original),
          originalPrice: parseFloat(item.preco_original),
          customPrice: item.preco_customizado ? parseFloat(item.preco_customizado) : parseFloat(item.preco_original),
          discount: parseFloat(item.desconto) || 0,
          discountType: item.tipo_desconto || 'value',
          quantity: item.quantidade,
          category: categoryFromExtras || item.caex_produtos.categoria_id, // Priorizar category dos dados extras
          estoque: item.caex_produtos.estoque,
          visivel_no_catalogo: item.caex_produtos.visivel_no_catalogo,
          // Adicionar dados extras específicos
          ...(dadosExtras.credencialData && { credencialData: dadosExtras.credencialData }),
          ...(dadosExtras.montadorData && { montadorData: dadosExtras.montadorData }),
          ...(dadosExtras.prestadorData && { prestadorData: dadosExtras.prestadorData }),
          cartItemId: item.id
        };
      });

      setCart(formattedCart);

      // Auto-preencher dados baseado na prioridade
      await autoFillCustomerData(userExpositorData, formattedCart, expositoresData || []);

    } catch (error) {
      console.error('Erro ao carregar dados iniciais:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  }, [user, navigate, checkUserExpositorData]);

  // Função otimizada para auto-preenchimento com prioridades
  const autoFillCustomerData = useCallback(async (userExpositorData, cartData, expositoresData) => {
    try {
      // PRIORIDADE 1: Se usuário é expositor logado
      if (userExpositorData) {
        console.log('Auto-preenchendo com dados do expositor logado');
        
        setCustomerInfo({
          company: userExpositorData.id.toString(),
          contact: userExpositorData.responsavel,
          email: userExpositorData.email,
          phone: userExpositorData.telefone || '',
          event: '',
          notes: ''
        });
        
        setDataSource('logged_expositor');
        setFieldsLocked(true); // Bloquear campos para expositor logado
        
        // Buscar eventos vinculados
        await fetchEventosVinculados(userExpositorData.id);
        return;
      }

      // PRIORIDADE 2: Credenciais no carrinho
      const credencialItems = cartData.filter(item => 
        item.category === 'Credencial' && 
        item.credencialData && 
        item.credencialData.expositor_id
      );
      
      if (credencialItems.length > 0) {
        const expositorId = credencialItems[0].credencialData.expositor_id;
        const expositor = expositoresData.find(exp => exp.id === expositorId);
        
        if (expositor) {
          console.log('Auto-preenchendo com dados da credencial no carrinho');
          
          setCustomerInfo({
            company: expositor.id.toString(),
            contact: expositor.responsavel,
            email: expositor.email,
            phone: expositor.telefone || '',
            event: credencialItems[0].credencialData.evento_id?.toString() || '',
            notes: ''
          });
          
          setDataSource('cart_credential');
          setFieldsLocked(false);
          
          await fetchEventosVinculados(expositor.id);
          return;
        }
      }

      // PRIORIDADE 3: Montadores no carrinho
      const montadorItems = cartData.filter(item => 
        item.category === 'Montador' && 
        item.montadorData && 
        item.montadorData.expositor_id
      );
      
      if (montadorItems.length > 0) {
        const expositorId = montadorItems[0].montadorData.expositor_id;
        const expositor = expositoresData.find(exp => exp.id === expositorId);
        
        if (expositor) {
          console.log('Auto-preenchendo com dados do montador no carrinho');
          
          setCustomerInfo({
            company: expositor.id.toString(),
            contact: expositor.responsavel,
            email: expositor.email,
            phone: expositor.telefone || '',
            event: montadorItems[0].montadorData.evento_id?.toString() || '',
            notes: ''
          });
          
          setDataSource('cart_montador');
          setFieldsLocked(false);
          
          await fetchEventosVinculados(expositor.id);
          return;
        }
      }

      // PRIORIDADE 4: Prestadores no carrinho (NOVO)
      const prestadorItems = cartData.filter(item => 
        item.category === 'Prestador' && 
        item.prestadorData && 
        item.prestadorData.expositor_id
      );
      
      if (prestadorItems.length > 0) {
        const expositorId = prestadorItems[0].prestadorData.expositor_id;
        const expositor = expositoresData.find(exp => exp.id === expositorId);
        
        if (expositor) {
          console.log('Auto-preenchendo com dados do prestador no carrinho');
          
          setCustomerInfo({
            company: expositor.id.toString(),
            contact: expositor.responsavel,
            email: expositor.email,
            phone: expositor.telefone || '',
            event: prestadorItems[0].prestadorData.evento_id?.toString() || '',
            notes: ''
          });
          
          setDataSource('cart_prestador');
          setFieldsLocked(false);
          
          await fetchEventosVinculados(expositor.id);
          return;
        }
      }

      // Se chegou aqui, não há dados para auto-preencher
      setDataSource('manual');
      setFieldsLocked(false);
      
    } catch (error) {
      console.error('Erro no auto-preenchimento:', error);
      setDataSource('manual');
      setFieldsLocked(false);
    }
  }, []);

  // Carregar dados iniciais apenas uma vez
  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);

  // Função para buscar eventos vinculados (otimizada)
  const fetchEventosVinculados = useCallback(async (expositorId) => {
    if (!expositorId) {
      setEventosVinculados([]);
      return;
    }
    
    try {
      const { data, error } = await supabase
        .from('caex_expositor_evento')
        .select(`
          evento_id,
          caex_eventos (
            id,
            nome,
            data_inicio,
            data_fim,
            ativo
          )
        `)
        .eq('expositor_id', parseInt(expositorId));
      
      if (error) throw error;
      
      if (data && data.length > 0) {
        const today = new Date().toISOString().split('T')[0];
        const eventosVinculados = data
          .map(item => item.caex_eventos)
          .filter(evento => 
            evento && 
            evento.ativo && 
            evento.data_inicio >= today
          )
          .sort((a, b) => new Date(a.data_inicio) - new Date(b.data_inicio));
        
        setEventosVinculados(eventosVinculados);
        
        // Se houver apenas um evento, selecionar automaticamente
        if (eventosVinculados.length === 1) {
          setCustomerInfo(prev => ({
            ...prev,
            event: eventosVinculados[0].id.toString()
          }));
        }
      } else {
        setEventosVinculados([]);
      }
    } catch (error) {
      console.error('Erro ao buscar eventos vinculados:', error);
      setEventosVinculados([]);
    }
  }, []);

  // Otimizar função de atualização do carrinho
  const updateCartItemQuantityDatabase = useCallback(async (productId, quantity) => {
    if (!user?.id) return false;

    if (quantity <= 0) {
      return removeFromCartDatabase(productId);
    }

    try {
      const { error } = await supabase
        .from('caex_carrinho_pedido')
        .update({ 
          quantidade: quantity,
          updated_at: new Date().toISOString()
        })
        .eq('usuario_id', user.id)
        .eq('produto_id', productId);

      if (error) throw error;

      setCart(prevCart =>
        prevCart.map(item =>
          item.id === productId ? { ...item, quantity } : item
        )
      );

      return true;
    } catch (error) {
      console.error('Erro ao atualizar quantidade:', error);
      alert('Não foi possível atualizar a quantidade. Tente novamente.');
      return false;
    }
  }, [user]);

  // NOVA FUNÇÃO: Limpar apenas o carrinho sem deletar registros vinculados
  const clearCartOnlyDatabase = useCallback(async () => {
    if (!user?.id) return false;

    try {
      // Remove apenas os itens do carrinho, sem deletar registros vinculados
      const { error } = await supabase
        .from('caex_carrinho_pedido')
        .delete()
        .eq('usuario_id', user.id);

      if (error) throw error;

      // Limpa apenas o estado local
      clearCartState();

      return true;
    } catch (error) {
      console.error('Erro ao limpar carrinho:', error);
      alert('Não foi possível limpar o carrinho. Tente novamente.');
      return false;
    }
  }, [user]);

  // Função modificada para limpar carrinho com controle de deleção de registros vinculados
  const clearCartDatabase = useCallback(async (deleteLinkedRecords = true) => {
    if (!user?.id) return false;

    try {
      if (deleteLinkedRecords) {
        const { data: cartItems } = await supabase
          .from('caex_carrinho_pedido')
          .select(`
            produto_id,
            dados_extras,
            caex_produtos (
              vinculado_credencial_expositor,
              vinculado_montador,
              vinculado_prestador
            )
          `)
          .eq('usuario_id', user.id);

        const promises = [];

        // Remover registros vinculados apenas se deleteLinkedRecords for true
        if (cartItems) {
          cartItems.forEach(cartItem => {
            const produto = cartItem.caex_produtos;
            const dadosExtras = cartItem.dados_extras || {};

            // Credenciais de expositor
            if (produto?.vinculado_credencial_expositor && dadosExtras.credencialData?.id) {
              promises.push(
                supabase
                  .from('caex_credenciais')
                  .delete()
                  .eq('id', dadosExtras.credencialData.id)
              );
            }

            // Montadores
            if (produto?.vinculado_montador && dadosExtras.montadorData?.id) {
              promises.push(
                supabase
                  .from('caex_montadores')
                  .delete()
                  .eq('id', dadosExtras.montadorData.id)
              );
            }

            // Prestadores
            if (produto?.vinculado_prestador && dadosExtras.prestadorData?.id) {
              promises.push(
                supabase
                  .from('caex_prestador')
                  .delete()
                  .eq('id', dadosExtras.prestadorData.id)
              );
            }
          });
        }

        // Executar deleção de registros vinculados se houver
        if (promises.length > 0) {
          await Promise.all(promises);
        }
      }

      // Limpar carrinho
      const { error } = await supabase
        .from('caex_carrinho_pedido')
        .delete()
        .eq('usuario_id', user.id);

      if (error) throw error;

      setCart([]);
      setDataSource('manual');
      setFieldsLocked(false);
      setCustomerInfo({
        company: '',
        contact: '',
        email: '',
        phone: '',
        event: '',
        notes: ''
      });
      setEventosVinculados([]);

      return true;
    } catch (error) {
      console.error('Erro ao limpar carrinho:', error);
      alert('Não foi possível limpar o carrinho. Tente novamente.');
      return false;
    }
  }, [user]);

  // Função modificada para remover do carrinho com controle de deleção de registros vinculados
  const removeFromCartDatabase = useCallback(async (productId, deleteLinkedRecords = true) => {
    if (!user?.id) return false;

    try {
      if (deleteLinkedRecords) {
        const [productResult, cartItemResult] = await Promise.all([
          supabase
            .from('caex_produtos')
            .select('vinculado_credencial_expositor, vinculado_montador, vinculado_prestador, nome')
            .eq('id', productId)
            .single(),
          supabase
            .from('caex_carrinho_pedido')
            .select('dados_extras')
            .eq('usuario_id', user.id)
            .eq('produto_id', productId)
            .single()
        ]);

        const { data: productData } = productResult;
        const { data: cartItemData } = cartItemResult;
        const dadosExtras = cartItemData?.dados_extras || {};

        // Remover registros vinculados se necessário
        const promises = [];

        // Credenciais de expositor
        if (productData?.vinculado_credencial_expositor && dadosExtras.credencialData?.id) {
          promises.push(
            supabase
              .from('caex_credenciais')
              .delete()
              .eq('id', dadosExtras.credencialData.id)
          );
        }

        // Montadores
        if (productData?.vinculado_montador && dadosExtras.montadorData?.id) {
          promises.push(
            supabase
              .from('caex_montadores')
              .delete()
              .eq('id', dadosExtras.montadorData.id)
          );
        }

        // Prestadores
        if (productData?.vinculado_prestador && dadosExtras.prestadorData?.id) {
          promises.push(
            supabase
              .from('caex_prestador')
              .delete()
              .eq('id', dadosExtras.prestadorData.id)
          );
        }

        // Executar deleção de registros vinculados se houver
        if (promises.length > 0) {
          await Promise.all(promises);
        }
      }

      // Remover item do carrinho
      const { error } = await supabase
        .from('caex_carrinho_pedido')
        .delete()
        .eq('usuario_id', user.id)
        .eq('produto_id', productId);

      if (error) throw error;

      setCart(prevCart => prevCart.filter(item => item.id !== productId));
      return true;

    } catch (error) {
      console.error('Erro ao remover item:', error);
      alert('Não foi possível remover o item. Tente novamente.');
      return false;
    }
  }, [user]);

  // NOVA FUNÇÃO: Limpar apenas o estado local (não mexe no banco de dados)
  const clearCartState = useCallback(() => {
    setCart([]);
    setDataSource('manual');
    setFieldsLocked(false);
    setCustomerInfo({
      company: '',
      contact: '',
      email: '',
      phone: '',
      event: '',
      notes: ''
    });
    setEventosVinculados([]);
  }, []);

  // Handlers otimizados
  const handleExpositorChange = useCallback(async (expositorId) => {
    if (!expositorId) {
      setCustomerInfo(prev => ({
        ...prev,
        company: '',
        contact: '',
        email: '',
        phone: '',
        event: ''
      }));
      setEventosVinculados([]);
      setDataSource('manual');
      setFieldsLocked(false);
      return;
    }
    
    const selectedExpositor = expositores.find(expositor => expositor.id === parseInt(expositorId));
    if (selectedExpositor) {
      setCustomerInfo(prev => ({
        ...prev,
        company: expositorId,
        contact: selectedExpositor.responsavel,
        email: selectedExpositor.email,
        phone: selectedExpositor.telefone || '',
        event: ''
      }));
      
      await fetchEventosVinculados(expositorId);
      setDataSource('manual');
      setFieldsLocked(false);
    }
    setDropdownOpen(false);
  }, [expositores, fetchEventosVinculados]);

  // Memoized values para performance
  const formatCurrency = useCallback((value) => {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  }, []);

  const getItemFinalPrice = useCallback((item) => {
    const basePrice = item.customPrice || item.price;
    const discount = item.discount || 0;
    
    if (discount === 0) return basePrice;
    
    if (item.discountType === 'percentage') {
      return basePrice * (1 - discount / 100);
    } else {
      return Math.max(0, basePrice - discount);
    }
  }, []);

  const calculateCartTotal = useMemo(() => {
    return cart.reduce((total, item) => {
      const finalPrice = getItemFinalPrice(item);
      return total + (finalPrice * item.quantity);
    }, 0);
  }, [cart, getItemFinalPrice]);

  const calculateTotalDiscount = useMemo(() => {
    return cart.reduce((totalDiscount, item) => {
      const basePrice = item.customPrice || item.price;
      const finalPrice = getItemFinalPrice(item);
      const itemDiscount = (basePrice - finalPrice) * item.quantity;
      return totalDiscount + itemDiscount;
    }, 0);
  }, [cart, getItemFinalPrice]);

  const getTotalItems = useMemo(() => {
    return cart.reduce((total, item) => total + item.quantity, 0);
  }, [cart]);

  const filteredExpositores = useMemo(() => {
    return searchTerm 
      ? expositores.filter(exp => 
          exp.nome_empresa.toLowerCase().includes(searchTerm.toLowerCase()) ||
          exp.cnpj.includes(searchTerm)
        )
      : expositores;
  }, [expositores, searchTerm]);

  const validateForm = useMemo(() => {
    return customerInfo.company && 
           customerInfo.contact && 
           customerInfo.email && 
           customerInfo.phone && 
           customerInfo.event;
  }, [customerInfo]);

  // Otimizar event handlers
  const updateCartItemQuantity = useCallback(async (itemId, quantity) => {
    await updateCartItemQuantityDatabase(itemId, quantity);
  }, [updateCartItemQuantityDatabase]);

  // Handlers atualizados
  const removeFromCart = useCallback(async (itemId) => {
    // Remove do carrinho E deleta registros vinculados (comportamento padrão)
    await removeFromCartDatabase(itemId, true);
  }, [removeFromCartDatabase]);

  const clearCart = useCallback(async () => {
    // Limpa carrinho E deleta registros vinculados (comportamento padrão)
    await clearCartDatabase(true);
  }, [clearCartDatabase]);

  const submitOrder = useCallback((e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    if (!validateForm) {
      alert('Por favor, preencha todos os campos obrigatórios.');
      return;
    }

    if (cart.length === 0) {
      alert('Adicione itens ao carrinho antes de finalizar o pedido.');
      return;
    }

    setShowConfirmModal(true);
  }, [validateForm, cart.length]);

  // NOVA FUNÇÃO: Atualizar prestadores com pedido_id
  const updatePrestadoresWithPedidoId = useCallback(async (pedidoId) => {
    try {
      // Buscar prestadores no carrinho
      const prestadoresNoCarrinho = cart.filter(item => 
        item.category === 'Prestador' && 
        item.prestadorData && 
        item.prestadorData.id
      );

      if (prestadoresNoCarrinho.length > 0) {
        const prestadorIds = prestadoresNoCarrinho.map(item => item.prestadorData.id);
        
        console.log(`Vinculando ${prestadorIds.length} prestadores ao pedido ${pedidoId}`);
        
        // Atualizar todos os prestadores do carrinho com o pedido_id
        const { error } = await supabase
          .from('caex_prestador')
          .update({ 
            pedido_id: pedidoId,
            updated_at: new Date().toISOString()
          })
          .in('id', prestadorIds);
        
        if (error) {
          console.error('Erro ao vincular prestadores ao pedido:', error);
          throw error;
        }
        
        console.log(`Prestadores vinculados com sucesso ao pedido ${pedidoId}`);
      }
    } catch (error) {
      console.error('Erro ao atualizar prestadores com pedido_id:', error);
      // Não interromper o fluxo, apenas logar o erro
    }
  }, [cart]);

  // FUNÇÃO CORRIGIDA: Atualizar montadores com pedido_id
  const updateMontadoresWithPedidoId = useCallback(async (pedidoId) => {
    try {
      console.log('Iniciando atualização de montadores com pedido_id:', pedidoId);
      console.log('Cart atual:', cart);
      
      // Buscar montadores no carrinho - CORRIGINDO A LÓGICA DE FILTRO
      const montadoresNoCarrinho = cart.filter(item => {
        // Verificar se o item tem category "Montador" E dados de montador
        const temCategoryMontador = item.category === 'Montador';
        const temMontadorData = item.montadorData;
        
        console.log(`Item ${item.id}: category=${item.category}, temMontadorData=${!!temMontadorData}`);
        
        return temCategoryMontador && temMontadorData;
      });

      console.log('Montadores encontrados no carrinho:', montadoresNoCarrinho);

      if (montadoresNoCarrinho.length > 0) {
        // Coletar todos os IDs de montadores (lidando com array ou objeto único)
        const montadorIds = [];
        
        montadoresNoCarrinho.forEach(item => {
          if (Array.isArray(item.montadorData)) {
            // Se montadorData é array (múltiplos montadores)
            item.montadorData.forEach(montador => {
              if (montador.id) {
                montadorIds.push(montador.id);
              }
            });
          } else if (item.montadorData && item.montadorData.id) {
            // Se montadorData é objeto único
            montadorIds.push(item.montadorData.id);
          }
        });
        
        console.log(`IDs de montadores coletados:`, montadorIds);
        
        if (montadorIds.length > 0) {
          console.log(`Vinculando ${montadorIds.length} montadores ao pedido ${pedidoId}`);
          
          // Atualizar todos os montadores do carrinho com o pedido_id
          const { data, error } = await supabase
            .from('caex_montadores')
            .update({ 
              pedido_id: pedidoId,
              updated_at: new Date().toISOString()
            })
            .in('id', montadorIds)
            .select('id, nome, pedido_id'); // Adicionar select para verificar o resultado
          
          if (error) {
            console.error('Erro ao vincular montadores ao pedido:', error);
            throw error;
          }
          
          console.log(`Montadores vinculados com sucesso ao pedido ${pedidoId}:`, data);
        } else {
          console.log('Nenhum ID de montador válido encontrado');
        }
      } else {
        console.log('Nenhum montador encontrado no carrinho');
      }
    } catch (error) {
      console.error('Erro ao atualizar montadores com pedido_id:', error);
      // Não interromper o fluxo, apenas logar o erro
    }
  }, [cart]);

  // NOVA FUNÇÃO: Atualizar credenciais com pedido_id (se necessário no futuro)
 // FUNÇÃO CORRIGIDA: Atualizar credenciais com pedido_id
const updateCredenciaisWithPedidoId = useCallback(async (pedidoId) => {
  try {
    console.log('Iniciando atualização de credenciais com pedido_id:', pedidoId);
    console.log('Cart atual:', cart);
    
    // Buscar credenciais no carrinho - CORRIGINDO A LÓGICA DE FILTRO
    const credenciaisNoCarrinho = cart.filter(item => {
      // Verificar se o item tem category "Credencial" E dados de credencial
      const temCategoryCredencial = item.category === 'Credencial';
      const temCredencialData = item.credencialData;
      
      console.log(`Item ${item.id}: category=${item.category}, temCredencialData=${!!temCredencialData}`);
      
      return temCategoryCredencial && temCredencialData;
    });

    console.log('Credenciais encontradas no carrinho:', credenciaisNoCarrinho);

    if (credenciaisNoCarrinho.length > 0) {
      // Coletar todos os IDs de credenciais (lidando com array ou objeto único)
      const credencialIds = [];
      
      credenciaisNoCarrinho.forEach(item => {
        if (Array.isArray(item.credencialData)) {
          // Se credencialData é array (múltiplas credenciais)
          item.credencialData.forEach(credencial => {
            if (credencial.id) {
              credencialIds.push(credencial.id);
            }
          });
        } else if (item.credencialData && item.credencialData.id) {
          // Se credencialData é objeto único
          credencialIds.push(item.credencialData.id);
        }
      });
      
      console.log(`IDs de credenciais coletados:`, credencialIds);
      
      if (credencialIds.length > 0) {
        console.log(`Vinculando ${credencialIds.length} credenciais ao pedido ${pedidoId}`);
        
        // Atualizar todas as credenciais do carrinho com o pedido_id
        const { data, error } = await supabase
          .from('caex_credenciais')
          .update({ 
            pedido_id: pedidoId,
            updated_at: new Date().toISOString()
          })
          .in('id', credencialIds)
          .select('id, nome, pedido_id'); // Adicionar select para verificar o resultado
        
        if (error) {
          console.error('Erro ao vincular credenciais ao pedido:', error);
          throw error;
        }
        
        console.log(`Credenciais vinculadas com sucesso ao pedido ${pedidoId}:`, data);
      } else {
        console.log('Nenhum ID de credencial válido encontrado');
      }
    } else {
      console.log('Nenhuma credencial encontrada no carrinho');
    }
  } catch (error) {
    console.error('Erro ao atualizar credenciais com pedido_id:', error);
    // Não interromper o fluxo, apenas logar o erro
  }
}, [cart]);


// FUNÇÃO AUXILIAR: Verificar se a tabela caex_credenciais tem o campo pedido_id
const verifyCredenciaisTableStructure = useCallback(async () => {
  try {
    console.log('Verificando estrutura da tabela caex_credenciais...');
    
    // Tentar fazer um select simples para verificar se a coluna pedido_id existe
    const { data, error } = await supabase
      .from('caex_credenciais')
      .select('id, pedido_id')
      .limit(1);
    
    if (error) {
      if (error.message.includes('column "pedido_id" does not exist')) {
        console.error('ERRO: A coluna pedido_id não existe na tabela caex_credenciais!');
        console.error('Você precisa adicionar a coluna pedido_id na tabela caex_credenciais:');
        console.error('ALTER TABLE caex_credenciais ADD COLUMN pedido_id INTEGER REFERENCES caex_pedidos(id);');
        return false;
      } else {
        console.error('Erro ao verificar tabela caex_credenciais:', error);
        return false;
      }
    }
    
    console.log('Estrutura da tabela caex_credenciais verificada com sucesso');
    return true;
  } catch (error) {
    console.error('Erro ao verificar estrutura da tabela:', error);
    return false;
  }
}, []);

// Atualizar o useEffect existente para incluir a verificação da tabela de credenciais
useEffect(() => {
  if (user?.id) {
    verifyMontadoresTableStructure();
    verifyCredenciaisTableStructure(); // Adicionar esta linha
  }
}, [user, verifyMontadoresTableStructure, verifyCredenciaisTableStructure]);

  // Função confirmOrder modificada com logs de debug
  const confirmOrder = useCallback(async () => {
    try {
      console.log('=== INICIANDO CRIAÇÃO DE PEDIDO ===');
      console.log('Cart atual no confirmOrder:', cart);
      
      const orderData = {
        expositor_id: parseInt(customerInfo.company),
        evento_id: parseInt(customerInfo.event),
        valor_total: calculateCartTotal,
        observacoes: customerInfo.notes || null,
        status: 'pendente',
        usuario_id: user?.id || null,
        updated_at: new Date().toISOString()
      };

      console.log('Dados do pedido a ser criado:', orderData);

      const { data: pedidoData, error: pedidoError } = await supabase
        .from('caex_pedidos')
        .insert([orderData])
        .select();
      
      if (pedidoError) {
        console.error('Erro ao criar pedido:', pedidoError);
        throw pedidoError;
      }
      
      if (pedidoData && pedidoData.length > 0) {
        const pedidoId = pedidoData[0].id;
        console.log(`=== PEDIDO CRIADO COM SUCESSO - ID: ${pedidoId} ===`);
        
        // Criar itens do pedido
        const pedidoItens = cart.map(item => {
          const finalPrice = getItemFinalPrice(item);
          return {
            pedido_id: pedidoId,
            produto_id: item.id,
            quantidade: item.quantity,
            preco_unitario: finalPrice,
            preco_total: finalPrice * item.quantity
          };
        });
        
        console.log('Itens do pedido a serem criados:', pedidoItens);
        
        if (pedidoItens.length > 0) {
          const { error: itensError } = await supabase
            .from('caex_pedidos_itens')
            .insert(pedidoItens);
          
          if (itensError) {
            console.error('Erro ao inserir itens:', itensError);
          } else {
            console.log('Itens do pedido criados com sucesso');
          }
        }

  // IMPORTANTE: Atualizar montadores com o pedido_id ANTES de limpar o carrinho
console.log('=== INICIANDO ATUALIZAÇÃO DE MONTADORES ===');
await updateMontadoresWithPedidoId(pedidoId);

// Atualizar credenciais com o pedido_id ANTES de limpar o carrinho
console.log('=== INICIANDO ATUALIZAÇÃO DE CREDENCIAIS ===');
await updateCredenciaisWithPedidoId(pedidoId);

// Atualizar prestadores com o pedido_id  
console.log('=== INICIANDO ATUALIZAÇÃO DE PRESTADORES ===');
await updatePrestadoresWithPedidoId(pedidoId);
        
        console.log('=== FINALIZANDO PROCESSO ===');
      }
      
      setOrderSubmitted(true);
      setShowConfirmModal(false);
      
      // CORREÇÃO: Usar função que não deleta registros vinculados
      await clearCartOnlyDatabase();
      
      setTimeout(() => {
        navigate('/orders');
      }, 3000);
    } catch (error) {
      console.error('Erro ao criar pedido:', error);
      alert('Não foi possível criar o pedido. Por favor, tente novamente.');
      setShowConfirmModal(false);
    }
  }, [customerInfo, calculateCartTotal, cart, getItemFinalPrice, clearCartOnlyDatabase, navigate, user, updateMontadoresWithPedidoId, updateCredenciaisWithPedidoId, updatePrestadoresWithPedidoId]);

  const getExpositorName = useCallback((id) => {
    const expositor = expositores.find(exp => exp.id === parseInt(id));
    return expositor ? expositor.nome_empresa : '';
  }, [expositores]);

  const getEventoName = useCallback((id) => {
    const evento = eventos.find(evt => evt.id === parseInt(id));
    return evento ? evento.nome : '';
  }, [eventos]);

  // Event listener para dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Função para obter mensagem de auto-preenchimento
  const getAutoFillMessage = () => {
    switch (dataSource) {
      case 'logged_expositor':
        return 'Dados preenchidos automaticamente com base no seu perfil de expositor.';
      case 'cart_credential':
        return 'Dados preenchidos automaticamente baseados nas credenciais do carrinho.';
      case 'cart_montador':
        return 'Dados preenchidos automaticamente baseados nos montadores do carrinho.';
      case 'cart_prestador':
        return 'Dados preenchidos automaticamente baseados nos prestadores do carrinho.';
      default:
        return null;
    }
  };

  // Verificar se usuário está logado
  if (!user) {
    return (
      <div className="order-page-container">
        <div className="order-success-container">
          <div className="order-success-card">
            <div className="order-success-icon">
              <FaUser />
            </div>
            <h3 className="order-success-title">Acesso Restrito</h3>
            <p className="order-success-message">
              Você precisa estar logado para acessar esta página.
            </p>
            <Button 
              variant="primary" 
              onClick={() => navigate('/login')}
              className="mt-3"
            >
              Fazer Login
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (orderSubmitted) {
    return (
      <div className="order-page-container">
        <div className="order-success-container">
          <div className="order-success-card">
            <div className="order-success-icon">
              <FaCheck />
            </div>
            <h3 className="order-success-title">Pedido Enviado com Sucesso!</h3>
            <p className="order-success-message">
              Seu pedido foi enviado e está aguardando aprovação. 
            </p>
            <p className="order-success-redirect">
              Redirecionando para a lista de pedidos...
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="order-page-container">
      <div className="order-page-hub">
        {/* Header */}
        <div className="order-page-header">
          <div className="order-page-title-section">
            <div className="order-page-title-left">
              <button 
                className="order-page-back-button"
                onClick={() => navigate('/catalog')}
              >
                <FaArrowLeft /> Voltar ao Catálogo
              </button>
              <h1 className="order-page-main-title">Finalizar Pedido</h1>
            </div>
            
            <CartSummary 
              cart={cart}
              calculateCartTotal={() => calculateCartTotal}
              calculateTotalDiscount={() => calculateTotalDiscount}
              getTotalItems={() => getTotalItems}
              formatCurrency={formatCurrency}
            />
          </div>
        </div>

        <Row>
          {/* Cart Column */}
          <Col lg={8}>
            <Card className="order-page-content-card" style={{'--animation-delay': '0s'}}>
              <Card.Header className="order-card-header">
                <h5 className="order-card-title">
                  <FaBox className="order-card-icon" />
                  Itens do Pedido
                </h5>
              </Card.Header>
              <Card.Body className="order-card-body">
                {cart.length === 0 ? (
                  <div className="empty-cart">
                    <FaShoppingCart className="empty-cart-icon" />
                    <h5 className="empty-cart-title">Seu carrinho está vazio</h5>
                    <p className="empty-cart-text">Adicione itens do catálogo para continuar</p>
                  </div>
                ) : (
                  <>
                    {cart.map(item => (
                      <CartItem 
                        key={item.id} 
                        item={item} 
                        onUpdateQuantity={updateCartItemQuantity}
                        onRemove={removeFromCart}
                        formatCurrency={formatCurrency}
                        getItemFinalPrice={getItemFinalPrice}
                      />
                    ))}
                    
                    <div className="cart-footer">
                      <button 
                        className="cart-clear-btn"
                        onClick={clearCart}
                        type="button"
                      >
                        <FaTrash /> Limpar Carrinho
                      </button>
                      <div className="cart-total">
                        <h5 className="cart-total-price">
                          Total: {formatCurrency(calculateCartTotal)}
                        </h5>
                        {calculateTotalDiscount > 0 && (
                          <div className="cart-total-discount text-success">
                            <small>Desconto total: {formatCurrency(calculateTotalDiscount)}</small>
                          </div>
                        )}
                        <small className="cart-total-items">
                          {getTotalItems} {getTotalItems === 1 ? 'item' : 'itens'}
                        </small>
                      </div>
                    </div>
                  </>
                )}
              </Card.Body>
            </Card>
          </Col>

          {/* Customer Info Column */}
          <Col lg={4}>
            <Card className="order-page-content-card" style={{'--animation-delay': '0.2s'}}>
              <Card.Header className="order-card-header accent-primary">
                <h5 className="order-card-title order-card-title-white">
                  <FaUser className="order-card-icon order-card-icon-white" />
                  Informações do Pedido
                  {fieldsLocked && (
                    <FaLock className="ms-2" title="Campos bloqueados - usuário expositor" />
                  )}
                </h5>
              </Card.Header>
              <Card.Body className="order-card-body">
                {loading ? (
                  <div className="text-center p-4">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Carregando...</span>
                    </div>
                    <p className="mt-3">Carregando dados...</p>
                  </div>
                ) : error ? (
                  <Alert variant="danger">
                    {error}
                  </Alert>
                ) : (
                  <div>
                    {getAutoFillMessage() && (
                      <Alert variant="info" className="mb-3">
                        <small>
                          <strong>{getAutoFillMessage()}</strong>
                          {!fieldsLocked && ' Você pode alterá-los se necessário.'}
                          {fieldsLocked && ' Os campos estão bloqueados para sua segurança.'}
                        </small>
                      </Alert>
                    )}

                    <Form.Group className="order-form-group">
                      <Form.Label className="order-form-label">
                        Empresa *
                        {fieldsLocked && <FaLock className="ms-1" size={12} />}
                      </Form.Label>
                      <div className="modern-select-container" ref={dropdownRef}>
                        <div 
                          className={`modern-select-header ${fieldsLocked ? 'disabled' : ''}`}
                          onClick={() => !fieldsLocked && setDropdownOpen(!dropdownOpen)}
                        >
                          <FaSearch className="modern-select-icon" />
                          <input
                            type="text"
                            className="modern-select-search"
                            placeholder={customerInfo.company ? getExpositorName(customerInfo.company) : "Buscar expositor..."}
                            value={searchTerm}
                            onChange={(e) => {
                              if (!fieldsLocked) {
                                setSearchTerm(e.target.value);
                                setDropdownOpen(true);
                              }
                            }}
                            onClick={(e) => {
                              if (!fieldsLocked) {
                                e.stopPropagation();
                                setDropdownOpen(true);
                              }
                            }}
                            disabled={fieldsLocked}
                          />
                          {customerInfo.company && !fieldsLocked && (
                            <span 
                              className="modern-select-clear"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleExpositorChange('');
                                setSearchTerm('');
                              }}
                            >
                              ✕
                            </span>
                          )}
                        </div>
                        {dropdownOpen && !fieldsLocked && (
                          <div className="modern-select-dropdown">
                            {filteredExpositores.length === 0 ? (
                              <div className="modern-select-no-results">
                                Nenhum expositor encontrado
                              </div>
                            ) : (
                              filteredExpositores.map(expositor => (
                                <div 
                                  key={expositor.id} 
                                  className={`modern-select-option ${parseInt(customerInfo.company) === expositor.id ? 'selected' : ''}`}
                                  onClick={() => handleExpositorChange(expositor.id.toString())}
                                >
                                  <div className="modern-select-option-content">
                                    <div className="modern-select-option-name">{expositor.nome_empresa}</div>
                                    <div className="modern-select-option-details">CNPJ: {expositor.cnpj}</div>
                                  </div>
                                </div>
                              ))
                            )}
                          </div>
                        )}
                      </div>
                    </Form.Group>

                    <Form.Group className="order-form-group">
                      <Form.Label className="order-form-label">
                        Pessoa de Contato *
                        {fieldsLocked && <FaLock className="ms-1" size={12} />}
                      </Form.Label>
                      <Form.Control
                        className="order-form-control"
                        type="text"
                        placeholder="Nome do responsável"
                        value={customerInfo.contact}
                        onChange={(e) => setCustomerInfo({...customerInfo, contact: e.target.value})}
                        disabled={fieldsLocked}
                        required
                      />
                    </Form.Group>

                    <Form.Group className="order-form-group">
                      <Form.Label className="order-form-label">
                        Email *
                        {fieldsLocked && <FaLock className="ms-1" size={12} />}
                      </Form.Label>
                      <Form.Control
                        className="order-form-control"
                        type="email"
                        placeholder="email@empresa.com"
                        value={customerInfo.email}
                        onChange={(e) => setCustomerInfo({...customerInfo, email: e.target.value})}
                        disabled={fieldsLocked}
                        required
                      />
                    </Form.Group>

                    <Form.Group className="order-form-group">
                      <Form.Label className="order-form-label">
                        Telefone *
                        {fieldsLocked && <FaLock className="ms-1" size={12} />}
                      </Form.Label>
                      <Form.Control
                        className="order-form-control"
                        type="tel"
                        placeholder="(11) 99999-9999"
                        value={customerInfo.phone}
                        onChange={(e) => setCustomerInfo({...customerInfo, phone: e.target.value})}
                        disabled={fieldsLocked}
                        required
                      />
                    </Form.Group>

                    <Form.Group className="order-form-group">
                      <Form.Label className="order-form-label">Evento *</Form.Label>
                      <Form.Select
                        className="order-form-select"
                        value={customerInfo.event}
                        onChange={(e) => setCustomerInfo({...customerInfo, event: e.target.value})}
                        required
                        disabled={!customerInfo.company || eventosVinculados.length === 0}
                      >
                        <option value="">Selecione o evento</option>
                        {eventosVinculados.length > 0 ? (
                          eventosVinculados.map(evento => (
                            <option key={evento.id} value={evento.id}>
                              {evento.nome} ({new Date(evento.data_inicio).toLocaleDateString()} - {new Date(evento.data_fim).toLocaleDateString()})
                            </option>
                          ))
                        ) : customerInfo.company ? (
                          <option value="" disabled>
                            Nenhum evento futuro vinculado a este expositor
                          </option>
                        ) : null}
                      </Form.Select>
                      {customerInfo.company && eventosVinculados.length === 0 && (
                        <div className="text-danger mt-1">
                          <small>Este expositor não possui eventos futuros vinculados.</small>
                        </div>
                      )}
                    </Form.Group>

                    <Form.Group className="order-form-group">
                      <Form.Label className="order-form-label">Observações</Form.Label>
                      <Form.Control
                        className="order-form-control order-form-textarea"
                        as="textarea"
                        rows={3}
                        placeholder="Informações adicionais sobre o pedido..."
                        value={customerInfo.notes}
                        onChange={(e) => setCustomerInfo({...customerInfo, notes: e.target.value})}
                      />
                    </Form.Group>

                    <div className="d-grid">
                      <button 
                        className="order-submit-btn btn"
                        type="button"
                        onClick={submitOrder}
                        disabled={cart.length === 0 || !validateForm}
                      >
                        Enviar Pedido
                      </button>
                    </div>

                    <small className="text-muted mt-2 d-block">
                      * Campos obrigatórios
                    </small>
                  </div>
                )}
              </Card.Body>
            </Card>

            {/* Order Summary */}
            {cart.length > 0 && (
              <Card className="order-page-content-card" style={{'--animation-delay': '0.4s'}}>
                <Card.Header className="order-card-header">
                  <h5 className="order-card-title">
                    <FaFileAlt className="order-card-icon" />
                    Resumo do Pedido
                  </h5>
                </Card.Header>
                <Card.Body className="order-card-body">
                  <div className="order-summary-item">
                    <span className="order-summary-label">Subtotal:</span>
                    <span className="order-summary-value">
                      {formatCurrency(cart.reduce((total, item) => {
                        const basePrice = item.customPrice || item.price;
                        return total + (basePrice * item.quantity);
                      }, 0))}
                    </span>
                  </div>
                  {calculateTotalDiscount > 0 && (
                    <div className="order-summary-item">
                      <span className="order-summary-label">Desconto:</span>
                      <span className="order-summary-value text-success">
                        -{formatCurrency(calculateTotalDiscount)}
                      </span>
                    </div>
                  )}
                  <div className="order-summary-total">
                    <span className="order-summary-total-label">Total:</span>
                    <span className="order-summary-total-value">{formatCurrency(calculateCartTotal)}</span>
                  </div>
                  <p className="order-summary-note">
                    Os preços podem estar sujeitos a alterações até a aprovação final.
                  </p>
                </Card.Body>
              </Card>
            )}
          </Col>
        </Row>

        {/* Confirmation Modal */}
        <Modal 
          show={showConfirmModal} 
          onHide={() => setShowConfirmModal(false)} 
          centered
          className="order-confirm-modal"
        >
          <Modal.Header closeButton>
            <Modal.Title>Confirmar Pedido</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p>Tem certeza que deseja enviar este pedido?</p>
            <div className="order-confirm-summary">
              <h6>Resumo:</h6>
              <p><strong>Empresa:</strong> {getExpositorName(customerInfo.company)}</p>
              <p><strong>Contato:</strong> {customerInfo.contact}</p>
              <p><strong>Evento:</strong> {getEventoName(customerInfo.event)}</p>
              <p><strong>Total de itens:</strong> {getTotalItems}</p>
              {calculateTotalDiscount > 0 && (
                <p><strong>Desconto aplicado:</strong> {formatCurrency(calculateTotalDiscount)}</p>
              )}
              <p className="mb-0"><strong>Valor total:</strong> {formatCurrency(calculateCartTotal)}</p>
            </div>
            <Alert variant="info" className="mt-3 mb-0">
              <small>
                Após o envio, seu pedido será analisado e você receberá uma confirmação por email.
              </small>
            </Alert>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline-secondary" onClick={() => setShowConfirmModal(false)}>
              Cancelar
            </Button>
            <Button 
              variant="success" 
              onClick={confirmOrder}
              className="order-submit-btn"
              style={{ padding: '0.5rem 1.5rem' }}
            >
              Confirmar Pedido
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    </div>
  );
};

export default NewOrderPage;